import { Controller } from "@hotwired/stimulus"
import { DirectUpload } from "@rails/activestorage"
import Cropper from "cropperjs"

const MAX_FILE_SIZE = 5 * 1024 * 1024 // 5MB
const ACCEPT_TYPES = [ "image/png", "image/jpeg", "image/gif", "image/webp" ]
const CROP_SIZE = 512

export default class extends Controller {
  static targets = [
    "fileInput",
    "cameraInput",
    "dialog",
    "cropImage",
    "cropContainer",
    "backdrop",
    "avatarInput",
    "form",
    "progressContainer",
    "progressBar",
    "progressText",
    "errorMessage",
    "inlineError",
    "saveButton"
  ]

  static values = {
    directUploadUrl: String,
    memberPath: String,
    csrfToken: String
  }

  connect() {
    this.objectUrl = null
    this.cropper = null
    this.currentFile = null

    this.boundFileChange = this.handleFileChange.bind(this)
    this.fileInputTarget.addEventListener("change", this.boundFileChange)
    this.cameraInputTarget.addEventListener("change", this.boundFileChange)
  }

  disconnect() {
    this.fileInputTarget?.removeEventListener("change", this.boundFileChange)
    this.cameraInputTarget?.removeEventListener("change", this.boundFileChange)
    this.cleanup()
  }

  handleFileChange(event) {
    const file = event.target.files?.[0]
    if (!file) return

    if (!ACCEPT_TYPES.includes(file.type)) {
      this.showInlineError("Please choose a PNG, JPEG, GIF, or WebP image.")
      return
    }
    if (file.size > MAX_FILE_SIZE) {
      this.showInlineError("Image must be less than 5MB.")
      return
    }
    this.hideInlineError()

    this.currentFile = file
    this.cleanup()
    this.objectUrl = URL.createObjectURL(file)
    this.cropImageTarget.src = this.objectUrl
    this.cropImageTarget.onload = () => {
      this.cropper = new Cropper(this.cropImageTarget, {
        aspectRatio: 1,
        viewMode: 1,
        dragMode: "move",
        autoCropArea: 0.8,
        restore: false,
        guides: true,
        center: true,
        highlight: false,
        cropBoxMovable: true,
        cropBoxResizable: true,
        toggleDragModeOnDblclick: false
      })
      this.hideError()
      this.dialogTarget.showModal()
    }
  }

  cancel(event) {
    if (event) event.preventDefault()
    this.cleanup()
    this.resetInputs()
    this.dialogTarget.close()
  }

  backdropClose(event) {
    if (event.target === this.backdropTarget) {
      this.cancel(event)
    }
  }

  save(event) {
    event.preventDefault()
    if (!this.cropper) return

    const canvas = this.cropper.getCroppedCanvas({ width: CROP_SIZE, height: CROP_SIZE })
    if (!canvas) return

    this.saveButtonTarget.disabled = true
    this.hideError()

    canvas.toBlob(
      (blob) => {
        if (!blob) {
          this.showError("Failed to process image.")
          this.saveButtonTarget.disabled = false
          return
        }
        const file = new File([ blob ], "avatar.jpg", { type: "image/jpeg" })
        this.uploadAndSubmit(file)
      },
      "image/jpeg",
      0.9
    )
  }

  uploadAndSubmit(file) {
    this.progressContainerTarget.style.display = "flex"
    this.progressBarTarget.style.width = "0%"
    this.progressTextTarget.textContent = "0%"

    const upload = new DirectUpload(file, this.directUploadUrlValue, {
      directUploadWillStoreFileWithXHR: (request) => {
        request.upload.addEventListener("progress", (event) => {
          if (event.lengthComputable) {
            const percent = Math.round((event.loaded / event.total) * 100)
            this.progressBarTarget.style.width = `${percent}%`
            this.progressTextTarget.textContent = `${percent}%`
          }
        })
      }
    })

    upload.create((error, blob) => {
      this.progressContainerTarget.style.display = "none"
      this.saveButtonTarget.disabled = false

      if (error) {
        this.showError("Upload failed. Please try again.")
        return
      }

      this.avatarInputTarget.value = blob.signed_id
      this.cleanup()
      this.resetInputs()
      this.dialogTarget.close()
      this.formTarget.requestSubmit()
    })
  }

  cleanup() {
    if (this.cropper) {
      this.cropper.destroy()
      this.cropper = null
    }
    if (this.objectUrl) {
      URL.revokeObjectURL(this.objectUrl)
      this.objectUrl = null
    }
    this.currentFile = null
  }

  resetInputs() {
    this.fileInputTarget.value = ""
    this.cameraInputTarget.value = ""
  }

  showError(message) {
    this.errorMessageTarget.textContent = message
    this.errorMessageTarget.classList.remove("hidden")
  }

  hideError() {
    this.errorMessageTarget.textContent = ""
    this.errorMessageTarget.classList.add("hidden")
  }

  showInlineError(message) {
    this.inlineErrorTarget.textContent = message
    this.inlineErrorTarget.classList.remove("hidden")
  }

  hideInlineError() {
    this.inlineErrorTarget.textContent = ""
    this.inlineErrorTarget.classList.add("hidden")
  }
}
